import { useSyncExternalStore, useCallback } from 'react';

/**
 * A hook that returns whether a media query matches the current window state.
 * Optimized for React 18+ using useSyncExternalStore to prevent visual "tearing"
 * in concurrent rendering.
 *
 * @param query The media query string (e.g., '(max-width: 768px)')
 * @returns boolean indicating if the query matches
 */
export const useMediaQuery = (query: string): boolean => {
  const subscribe = useCallback(
    (callback: () => void) => {
      const matchMedia = window.matchMedia(query);
      matchMedia.addEventListener('change', callback);
      return () => matchMedia.removeEventListener('change', callback);
    },
    [query]
  );

  const getSnapshot = () => {
    return window.matchMedia(query).matches;
  };

  const getServerSnapshot = () => {
    // Default to false for initial render (SSR/Hydration context)
    // Since we aren't using SSR, this is just a fallback for the first pass.
    return false;
  };

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};
